import { Link } from "react-router-dom";
import { Footer } from "./Home";

const UPDATED = "October 30, 2026";
const MAIL = <a href="mailto:info@deckloom.net">info@deckloom.net</a>;

/** A page of text: about, the privacy policy, the terms of service. */
function TextPage({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="text-page">
      <article>
        <p className="crumb">
          <Link to="/">deckloom</Link>
        </p>
        <h1>{title}</h1>
        {children}
      </article>
      <Footer />
    </main>
  );
}

export function AboutPage() {
  return (
    <TextPage title="About deckloom">
      <p>
        deckloom is a slide presentation platform. A PowerPoint file, a PDF, an image or a zip of images is converted into deckloom's own deck format, animations and
        embedded media included, and becomes a link. Everyone who opens the link follows the presenter in real time; the presenter can draw on the slides, keep
        notes, and let viewers draw too.
      </p>
      <p>
        deckloom.net is the platform: the conversion, the player and the HTTP API. Decks are made through the API with an account key, and every control the
        presenter has, moving through the deck, drawing, editing slides, is open to scripts, AI agents and other apps over the same API.{" "}
        <a href="https://deckloom.io">deckloom.io</a> is the app built on it, where people upload and present, with live sessions around the stage: a cursor, form
        pages, voice and cameras.
      </p>
      <p>
        It runs on Cloudflare: Workers, Durable Objects for the live rooms, D1 for records, R2 for files and a container for the conversion. Questions, ideas and
        trouble go to {MAIL}. If you find a security problem, please write to the same address with what you found and how to reproduce it; we answer, fix what is
        confirmed, and credit you if you wish.
      </p>
      <p>
        <Link to="/privacy">Privacy policy</Link> · <Link to="/terms">Terms of service</Link>
      </p>
    </TextPage>
  );
}

export function PrivacyPage() {
  return (
    <TextPage title="Privacy policy">
      <p className="muted">Last updated {UPDATED}.</p>
      <p>
        This policy says what deckloom (deckloom.net and deckloom.io, "deckloom", "we") collects, why, where it is kept and how to have it removed. It applies to
        everyone who uploads, presents or views a deck, runs or joins a session, uses the API, or writes to us.
      </p>

      <h2>1. What we collect</h2>
      <p>
        <strong>Decks.</strong> A file uploaded to deckloom is stored and converted, and the deck is kept until its owner deletes it: the original file, the
        converted deck, its media, and the drawings and notes made on it. Anything in the file, including text, images, sound and video, and speaker notes, is
        stored as part of the deck.
      </p>
      <p>
        <strong>Accounts.</strong> An account is a name and a key. There is no public signup; accounts are made by us for the apps and people who build on the
        API. Keys are stored only as a cryptographic hash, never in clear.
      </p>
      <p>
        <strong>Sessions.</strong> A session on deckloom.io keeps what is on stage, the forms the host wrote, the answers participants gave (with the name they
        typed, if any), the host's cursor while it moves, and which participants have their camera, microphone or hand up. Voice, camera and screen streams pass
        through Cloudflare Realtime and are not recorded.
      </p>
      <p>
        <strong>Viewers and participants.</strong> Opening a deck or joining a session needs no account. The pages keep a few things in your browser's local
        storage: a random participant id, draft drawings, the presenter tokens of decks and the host tokens of sessions you made in that browser, so that a reload
        keeps them. We set no advertising or tracking cookies.
      </p>
      <p>
        <strong>Mail.</strong> Mail sent to info@deckloom.net is stored, text and attachments, so that we can answer it.
      </p>
      <p>
        <strong>Logs.</strong> Requests are logged by Cloudflare, where deckloom runs, with the usual request data (address, time, path, user agent), for a short
        time, for operations and abuse prevention.
      </p>

      <h2>2. Why</h2>
      <p>
        To do what you asked: convert and show your deck, keep everyone on the same slide, carry drawings, notes, forms and media between the people in a room,
        answer your mail. To keep the service running and safe: rate limits, abuse prevention, debugging. For nothing else: we do not sell data, profile people or
        show advertising.
      </p>

      <h2>3. Who can see what</h2>
      <p>
        A deck is reachable by anyone who has its link; the link is a random id and is not listed anywhere. The presenter's link carries a token that must not be
        shared: whoever has it can present, draw and edit. A session's plain link is for participants; its host link carries the host token. Drawings and notes
        the presenter publishes are shown to everyone in the room; drafts stay in the presenter's browser until published. Answers to a form are shown to the
        host.
      </p>
      <p>
        Our providers see data as needed to host it: Cloudflare (Workers, D1, R2, Durable Objects, Realtime, Email Routing). Nobody else.
      </p>

      <h2>4. Where and how long</h2>
      <p>
        Everything runs on Cloudflare's network. Files are in Cloudflare R2, records in Cloudflare D1, live rooms and sessions in Durable Objects. A deck and
        what belongs to it stay until the owner deletes it. A session stays until the host ends it. Mail stays until the conversation is over and we delete it.
        Logs are kept for a short time by Cloudflare.
      </p>

      <h2>5. Deleting and asking</h2>
      <p>
        A deck's owner deletes it through the API or the app it was uploaded with; this removes its files, room, drawings and notes. A host ends a session from
        the app; this removes its forms and answers. To have anything else removed, or to ask what we hold about you, write to {MAIL}.
      </p>

      <h2>6. Children</h2>
      <p>deckloom is not directed at children under 13, and we do not knowingly collect their data. If you believe we hold some, write to us and we will remove it.</p>

      <h2>7. Changes</h2>
      <p>When this policy changes, the date at the top changes with it. Material changes are announced on the home page.</p>
    </TextPage>
  );
}

export function TermsPage() {
  return (
    <TextPage title="Terms of service">
      <p className="muted">Last updated {UPDATED}.</p>
      <p>
        These terms are the agreement between you and deckloom (deckloom.net and deckloom.io, "deckloom", "we") for using the service: the sites, the player,
        the API and the sessions. By using any of them you accept these terms. If you do not, do not use the service.
      </p>

      <h2>1. The service</h2>
      <p>
        deckloom converts slide decks and shows them live to the people who hold a link, with drawing, notes and live sessions, and offers the same controls over
        an HTTP API. The service is provided as it is; we improve it continually and may change or withdraw features.
      </p>

      <h2>2. Accounts and keys</h2>
      <p>
        Accounts are made by us for apps and people who build on the API; there is no public signup. An account's key, a deck's presenter token, a session's host
        token and any API key are secrets: you are responsible for keeping them and for everything done with them. If one leaks, make a new one (account keys and
        API keys can be renewed or revoked at any time) and tell us.
      </p>

      <h2>3. Your content</h2>
      <p>
        What you upload stays yours. You give us the permission needed to store it, convert it, and show it to the people who open its link, and nothing more. You
        confirm you have the right to upload and present it. You can delete it at any time, and deleting removes it from the service.
      </p>

      <h2>4. Acceptable use</h2>
      <p>
        Do not use deckloom to store or present content that is illegal, that infringes someone's rights, or that harms others (malware, harassment, material
        involving the abuse of minors). Do not try to get at other people's decks, sessions or keys, interfere with the service, or go around the limits of your
        plan. Automated use is welcome through the API, within its rate limits.
      </p>

      <h2>5. Plans and limits</h2>
      <p>
        Each account has a plan with limits: how many decks, how large an upload, how many slides, how many viewers in a room, which APIs. The API answers with a
        clear error when a limit is reached. There is no charge for the service at present; if that changes, the prices and terms will be published before they
        apply.
      </p>

      <h2>6. Availability</h2>
      <p>
        We try to keep deckloom up, but we do not promise it. It can be down for maintenance or for reasons outside our control, and live rooms depend on the
        network between you and Cloudflare. Keep your own copy of what you upload.
      </p>

      <h2>7. Ending</h2>
      <p>
        You can stop using the service at any time and delete your decks and sessions. We may suspend or close an account, or remove content, that breaks these
        terms, and will say why when we can. We may also end the service as a whole, with notice on the home page.
      </p>

      <h2>8. No warranty, limited liability</h2>
      <p>
        The service is provided "as is", without warranty of any kind, express or implied, including fitness for a particular purpose. To the extent the law
        allows, deckloom is not liable for indirect, incidental or consequential damages, or for loss of data or profits, arising from the use of or inability
        to use the service. Our total liability for any claim is limited to what you paid us for the service in the twelve months before the claim, which is
        nothing while the service is free.
      </p>

      <h2>9. Privacy</h2>
      <p>
        How we handle data is described in the <Link to="/privacy">privacy policy</Link>, which is part of these terms.
      </p>

      <h2>10. Changes and contact</h2>
      <p>
        When these terms change, the date at the top changes with it; using the service after a change means accepting it. Questions go to {MAIL}.
      </p>
    </TextPage>
  );
}
